import React from 'react';
import { COLORS } from '../../../styles/UISystem';
import { ButtonRegular } from '../Typography';
import { CaretRight } from '@phosphor-icons/react/dist/ssr/CaretRight';

/**
 * ButtonChatContact Component
 *
 * A button that appears in chat when a user shares their contact information.
 * Similar to a vCard in iMessage.
 *
 * @param {string} name - Contact name
 * @param {function} onClick - Click handler function
 * @param {object} style - Additional style overrides
 * @param {string} variant - 'received' or 'sent'
 * @param {boolean} disabled - Whether the button is disabled (for 'sent' variant)
 */
export const ButtonChatContact = ({
  name = 'Mark Mangan',
  onClick,
  style,
  variant = 'received',
  disabled = false,
  ...props
}) => {
  // Get first initial of name
  const firstInitial = name.charAt(0).toUpperCase();

  // Determine background color based on variant
  const backgroundColor = variant === 'received' ? COLORS.MEDIUM_GRAY : COLORS.MEDIUM_BLUE;

  // Determine if button should have arrow and be clickable
  const isClickable = variant === 'received' && !disabled;

  return (
    <button
      onClick={isClickable ? onClick : undefined}
      disabled={disabled || variant === 'sent'}
      style={{
        height: '40px', // Fixed height of 40px
        backgroundColor: backgroundColor,
        borderRadius: '8px', // Rounded corners instead of fully rounded sides
        border: 'none',
        cursor: isClickable ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        padding: '0',
        paddingLeft: '16px', // Space for text
        paddingRight: variant === 'sent' ? '7px' : '4px', // Add 3px to the right for sent variant
        ...style,
      }}
      {...props}
    >
      {/* Name text */}
      <ButtonRegular
        style={{
          color: COLORS.WHITE,
          marginRight: '8px',
        }}
      >
        {name}
      </ButtonRegular>

      {/* Circle with first initial */}
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: COLORS.WHITE,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: COLORS.DARK_GRAY, // Changed from MEDIUM_DARK_GRAY to DARK_GRAY
          fontSize: '14px',
          fontWeight: 'normal',
          marginRight: isClickable ? '8px' : '0',
        }}
      >
        {firstInitial}
      </div>

      {/* Arrow (only for received variant) - positioned after the circle */}
      {isClickable && (
        <CaretRight
          size={20} // Slightly larger arrow
          weight="bold"
          color={COLORS.WHITE}
          style={{ marginLeft: '-3px' }} // Move 3px closer to the circle
        />
      )}
    </button>
  );
};

/**
 * ButtonChatContactExample Component
 *
 * Example usage of the ButtonChatContact component.
 */
export const ButtonChatContactExample = () => {
  const handleClick = () => {
    console.log('ButtonChatContact clicked');
  };

  return (
    <div style={{ marginBottom: '60px' }}>
      <div
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: COLORS.DARK_GRAY,
          marginBottom: '32px',
          textAlign: 'center',
        }}
      >
        ButtonChatContact
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            fontSize: '16px',
            color: COLORS.DARK_GRAY,
            marginBottom: '16px',
            textAlign: 'center',
          }}
        >
          received
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '32px',
          }}
        >
          <ButtonChatContact name="Mark Mangan" variant="received" onClick={handleClick} />
        </div>

        <div
          style={{
            fontSize: '16px',
            color: COLORS.DARK_GRAY,
            marginBottom: '16px',
            textAlign: 'center',
          }}
        >
          sent
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '16px',
          }}
        >
          <ButtonChatContact name="Mark Mangan" variant="sent" disabled={true} />
        </div>
      </div>
    </div>
  );
};
